import flatten from 'lodash.flatten';

import {APPROVAL_TYPES} from '@api/constants';

import {
    FIELD_TYPES,
    REDIRECT_VIEW_KEY
} from '@constants';

import {
    isSessionStorageAllowed,
    setValueForKey,
    getValueForKey,
    valueFromTempStorage
} from '@utils/storageSelectors';

import {saveToTempStorage} from './functions';

export const getIsDownloadAllowed = (transaction = {}) => {
    const isCompleted = !!transaction?.completed_at;
    const disableDownloadForUncompletedPackage = transaction?.settings?.disable_download_incomplete_transaction === 'true';

    return !disableDownloadForUncompletedPackage || isCompleted;
};

const haveAllRolesSigned = (roles = []) => roles.every(({hasSigned}) => hasSigned);

export const isTransactionCompleted = (transaction = {}) => {
    if (!transaction) return false;
    return !!transaction.completed_at || transaction.isCompleted || haveAllRolesSigned(transaction.roles || []);
};
const getIsAttachmentCompleted = (status) => status === 'accepted' || status === 'in_progress';

const getFirstPendingUpload = (attachments) => attachments?.find(
    (attachment) => !getIsAttachmentCompleted(attachment.status)
);

const getPendingAttachments = (attachments) => attachments.filter(
    ({status}) => !getIsAttachmentCompleted(status)
);

export const nextPendingUpload = (attachments, currentAttachmentId) => {
    if (!currentAttachmentId) {
        return getFirstPendingUpload(attachments);
    }
    const pendingAttachments = getPendingAttachments(attachments);
    const currentIndex = pendingAttachments.findIndex(
        ({id}) => id === currentAttachmentId
    );
    const nextIndex = currentIndex + 1 >= pendingAttachments.length ? 0 : currentIndex + 1;

    return pendingAttachments[nextIndex];
};

export const isPendingUploads = (attachments, attachmentId) => {
    const pendingAttachments = getPendingAttachments(attachments);

    return pendingAttachments.length === 1
        ? pendingAttachments.some(({id}) => id !== attachmentId)
        : pendingAttachments.length !== 0;
};

export const getDocumentById = (documents = [], docId) => documents.find(({id}) => id === docId) || {};

export const getAttachmentById = (attachments = [], attachmentId) => attachments.find(({id}) => id === attachmentId) || {};

export const isAnyAttachmentNotUploaded = (attachments) => !attachments?.every(
    (attachment) => getIsAttachmentCompleted(attachment.status)
);

export const isEveryRequiredAttachmentUploaded = (attachments) => attachments
    ?.filter((attachment) => attachment.is_required)
    .every((attachment) => getIsAttachmentCompleted(attachment.status));

const getFieldOrGroupId = (field) => {
    if (field.isRadio) {
        return field.radioGroup.id;
    }
    if (field.isGroupCheckbox) {
        return field.checkboxGroup.id;
    }
    return field.id;
};

const getFieldsByApprovalId = ({fields, approvalId}) => fields.filter((field) => field.approval_id === approvalId);

export const getFieldsByPages = ({pages = []} = {}) => {
    const fields = pages.reduce((reducedFields, page) => {
        const signerFields = page.signerFields || [];
        reducedFields.push(signerFields.filter((flds) => !flds.isSignature));
        return reducedFields;
    }, []);

    return flatten(fields);
};

export const getApprovalRelatedErrors = (({approvalId, fieldsErrors, fields}) => {
    const approvalRelatedFields = getFieldsByApprovalId({
        fields,
        approvalId
    });

    return approvalRelatedFields.reduce((acc, field) => {
        const fieldId = getFieldOrGroupId(field);
        const containsField = acc.some((f) => {
            const fId = getFieldOrGroupId(f);
            return fId === fieldId;
        });
        const error = fieldsErrors[fieldId];

        if (!containsField && error) {
            acc.push(error);
        }

        return acc;
    }, []);
});

/**
 * Checks if a field or approval is enabled for interaction.
 * Supports both REST API format (type, is_disabled) and legacy GraphQL format (isInput, isSignature).
 * @param {any} field - Field or approval object
 * @param {any} [approval=null] - Optional approval for field with approval reference
 * @returns {boolean} - True if field/approval is enabled
 */
export const getIsFieldEnabled = (field, approval = null) => {
    const isDisabled = field.is_disabled ?? false;
    const fieldType = field.type?.toLowerCase();
    const signingType = field.signing_type?.toLowerCase();

    const isSignatureType = fieldType === FIELD_TYPES.SIGNATURE
        || signingType === APPROVAL_TYPES.SIGNATURE
        || signingType === APPROVAL_TYPES.CAPTURE
        || signingType === APPROVAL_TYPES.INITIALS
        || signingType === APPROVAL_TYPES.PARAPH;

    if (isSignatureType) {
        if (signingType) {
            return !isDisabled;
        }
        return approval ? !approval.is_disabled : !isDisabled;
    }

    const isLabelType = fieldType === FIELD_TYPES.LABEL;
    if (isLabelType) {
        return false;
    }

    return !isDisabled;
};

/**
 * @deprecated Use isFilled/isSigned from getFieldStatuses/getApprovalStatuses instead.
 * @param {any} field
 * @param {any} [approval=null]
 * @returns {boolean}
 */
export const isActionContainValue = (field, approval = null) => {
    const {
        isSelected, isCheckbox, isRadio, radioGroup, type, value, signing_type
    } = field;

    if (type === FIELD_TYPES.SIGNATURE || signing_type === APPROVAL_TYPES.SIGNATURE) {
        if (signing_type) {
            return !!field.signed_at;
        }
        return !!approval?.signed_at;
    }

    if (isCheckbox) {
        return isSelected;
    }
    if (type !== FIELD_TYPES.SIGNATURE && isRadio) {
        return radioGroup?.isSelected;
    }
    return !!value;
};

/**
 * @deprecated Use isInteractive from getFieldStatuses/getApprovalStatuses instead.
 * @param {any} field
 * @param {any} [approval=null]
 * @returns {boolean}
 */
export const isEnabledOrRequiredInputField = (field, approval = null) => {
    const fieldType = field.type?.toLowerCase();
    const isLabel = field.isLabel || fieldType === FIELD_TYPES.LABEL;
    const isCustomField = field.isCustomField;

    if (!field || isLabel || isCustomField) {
        return false;
    }

    const isEnabled = getIsFieldEnabled(field, approval);
    const isRequired = field.is_required || field.isRequired
        || (field.signing_type && !field.is_optional)
        || (approval && !approval.is_optional);

    return isEnabled || isRequired;
};

export const setAutoredirectIfAllowed = ({isFaceToFace = false} = {}) => {
    if (isFaceToFace) return;

    if (isSessionStorageAllowed()) {
        setValueForKey(REDIRECT_VIEW_KEY, true);
    } else {
        saveToTempStorage({
            key: REDIRECT_VIEW_KEY,
            value: true
        });
    }
};

/** @returns {boolean} */
export const getIsAutoRedirectAllowed = () => {
    if (isSessionStorageAllowed()) {
        return !!getValueForKey(REDIRECT_VIEW_KEY);
    }
    return valueFromTempStorage(REDIRECT_VIEW_KEY);
};

/**
 * Get fields for a specific document by traversing the approval chain.
 * Document -> Approvals (by document_id) -> Fields (by approval_id)
 * @param {string | null} documentId
 * @param {Record<string, Array<{id: string}>>} approvals
 * @param {Record<string, Array<any>>} fields
 * @returns {Array<any>}
 */
export const getFieldsForDocument = (documentId, approvals, fields) => {
    if (!documentId) return [];

    const documentApprovals = approvals[documentId] || [];
    if (documentApprovals.length === 0) return [];

    const result = [];
    for (const approval of documentApprovals) {
        const approvalFields = fields[approval.id];
        if (approvalFields && approvalFields.length > 0) {
            result.push(...approvalFields);
        }
    }
    return result;
};
